import MapboxDraw, {
  type DrawCustomMode,
  type DrawCustomModeThis,
  type DrawLineString,
} from '@mapbox/mapbox-gl-draw';
import { cloneDeep } from 'lodash';
import type { LineStringCoordinate } from './DirectSelectMode';
import { doubleClickZoom, isEventAtCoordinates } from './utils';

interface State {
  line: DrawLineString & { id: string };
  currentVertexPosition: number;
  addedCoordinates: Array<LineStringCoordinate>;
  direction: 'forward' | 'backwards';
}

type This = DrawCustomModeThis & ExpandLineStringModeType;
interface ExpandLineStringModeType extends DrawCustomMode<State> {
  onSetup: (
    this: This,
    opts: { featureId?: string; from: [number, number] | [number, number, number] },
  ) => State;
  onStop: (this: This, state: State) => void;
  clickAnywhere: (this: This, state: State, e: mapboxgl.MapMouseEvent) => void;
}

export const ExpandLineStringMode = cloneDeep(
  MapboxDraw.modes.draw_line_string,
) as ExpandLineStringModeType;

ExpandLineStringMode.onSetup = function (opts) {
  const baseState = MapboxDraw.modes.draw_line_string.onSetup?.call(this, opts);

  return {
    ...baseState,
    addedCoordinates: [],
  };
};

ExpandLineStringMode.clickAnywhere = function (state, e) {
  if (
    (state.currentVertexPosition > 0 &&
      isEventAtCoordinates(e, state.line.coordinates[state.currentVertexPosition - 1])) ||
    (state.direction === 'backwards' &&
      isEventAtCoordinates(e, state.line.coordinates[state.currentVertexPosition + 1]))
  ) {
    return this.changeMode('simple_select', { featureIds: [state.line.id] });
  }

  this.updateUIClasses({ mouse: 'add' });

  state.line.updateCoordinate(state.currentVertexPosition.toString(), e.lngLat.lng, e.lngLat.lat);
  if (state.direction === 'forward') {
    state.currentVertexPosition++;
    state.line.updateCoordinate(state.currentVertexPosition.toString(), e.lngLat.lng, e.lngLat.lat);
  } else {
    state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
  }

  // add the new coordinate to the state
  state.addedCoordinates.push({
    index: state.currentVertexPosition,
    lineStringId: state.line.id,
    lat: e.lngLat.lat,
    lng: e.lngLat.lng,
  });
};

ExpandLineStringMode.onStop = function (state) {
  doubleClickZoom.enable(this);
  this.activateUIButton();

  // check to see if we've deleted this feature
  if (this.getFeature(state.line.id) === undefined) return;

  // remove last added coordinate
  state.line.removeCoordinate(`${state.currentVertexPosition}`);
  if (state.line.isValid()) {
    this.map.fire('draw.update', {
      features: [state.line.toGeoJSON()],
      action: 'change_coordinates',
      coordinates: state.addedCoordinates,
      coordinateAction: 'add',
    });
  } else {
    this.deleteFeature(state.line.id, { silent: true });
    this.changeMode('simple_select', {}, { silent: true });
  }
};
